import React from "react";
import { createPortal } from "react-dom";
import { Save, X, AlertCircle } from "lucide-react";

/**
 * Reusable confirmation dialog for the Escape-with-unsaved-changes flow.
 * Rendered inside any modal that uses `useUnsavedGuard`.
 *
 * Props:
 *   open: bool — matches `confirming === "esc"`
 *   onSave / onDiscard / onKeep: handlers wired from the hook
 *   saving: bool — disables the Save button while a save is in flight
 */
export default function UnsavedChangesDialog({ open, onSave, onDiscard, onKeep, saving }) {
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[400] bg-[#040D1A]/80 backdrop-blur-sm flex items-center justify-center px-4"
      data-testid="unsaved-changes-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onKeep?.(); }}
    >
      <div className="card w-full max-w-md p-5">
        <div className="flex items-start gap-3">
          <AlertCircle size={20} className="text-amber-300 shrink-0 mt-0.5" />
          <div>
            <div className="font-display text-lg text-slate-100">Unsaved changes</div>
            <div className="text-[13px] text-slate-400 mt-1">
              This form has changes that haven&apos;t been saved yet. What would you like to do?
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
          <button onClick={onKeep} className="pill" data-testid="unsaved-keep">
            Keep editing
          </button>
          <button
            onClick={onDiscard}
            className="pill !border-rose-500/40 !text-rose-200 hover:!bg-rose-500/10"
            data-testid="unsaved-discard"
          >
            <X size={12} /> Discard changes
          </button>
          <button
            onClick={onSave}
            disabled={saving}
            className="pill active disabled:opacity-60"
            data-testid="unsaved-save"
          >
            <Save size={12} /> {saving ? "Saving…" : "Save & close"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
